import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Locale } from '../data/schema'
import {
  disableGoogleAnalytics,
  enableGoogleAnalytics,
  getAnalyticsConsent,
  saveAnalyticsConsent,
  type AnalyticsConsent,
} from '../analytics'
import { Icon } from './Icon'

export function AnalyticsConsentControl({ locale, promptEnabled = true }: { locale: Locale, promptEnabled?: boolean }) {
  const [consent, setConsent] = useState<AnalyticsConsent>(getAnalyticsConsent)
  const [open, setOpen] = useState(consent === null && promptEnabled)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const allowRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (consent === 'granted') enableGoogleAnalytics()
    else if (consent === 'denied') disableGoogleAnalytics()
  }, [consent])

  useEffect(() => {
    if (promptEnabled && consent === null) setOpen(true)
  }, [consent, promptEnabled])

  useEffect(() => {
    if (!open) return
    allowRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || consent === null) return
      setOpen(false)
      triggerRef.current?.focus()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [consent, open])

  const choose = (next: Exclude<AnalyticsConsent, null>) => {
    saveAnalyticsConsent(next)
    setConsent(next)
    setOpen(false)
  }

  const copy = locale === 'en' ? {
    trigger: 'Privacy settings',
    eyebrow: 'Privacy choice',
    title: 'Help us understand how MythWeave is used?',
    body: 'With your permission, Google Analytics measures aggregate visits and feature use. No analytics tag loads before you allow it. Your choice does not affect access or story ranking.',
    allow: 'Allow analytics',
    decline: 'Decline',
    policy: 'Privacy notice',
    close: 'Close privacy choices',
  } : {
    trigger: '私隱設定',
    eyebrow: '私隱選擇',
    title: '願意協助我們了解 MythWeave 的使用情況嗎？',
    body: '獲得你的同意後，Google Analytics 才會量度整體瀏覽及功能使用情況。同意前不會載入分析標籤；你的選擇不影響內容存取或故事排序。',
    allow: '允許分析',
    decline: '拒絕',
    policy: '私隱聲明',
    close: '關閉私隱選擇',
  }

  return <>
    <button ref={triggerRef} className="privacy-trigger" onClick={() => setOpen(true)} aria-label={copy.trigger}>
      <Icon name="privacy" /><span className="wide-label">{copy.trigger}</span>
    </button>
    {open && createPortal(
      <div className="analytics-consent-layer">
        <section className="analytics-consent" role="dialog" aria-modal="true" aria-labelledby="analytics-consent-title" data-testid="analytics-consent">
          {consent !== null && <button className="analytics-close" onClick={() => { setOpen(false); triggerRef.current?.focus() }} aria-label={copy.close}><Icon name="close" size={16} /></button>}
          <span className="eyebrow">{copy.eyebrow}</span>
          <h2 id="analytics-consent-title">{copy.title}</h2>
          <p>{copy.body}</p>
          <div className="analytics-consent-actions">
            <button ref={allowRef} className="analytics-allow" onClick={() => choose('granted')}>{copy.allow}</button>
            <button className="analytics-decline" onClick={() => choose('denied')}>{copy.decline}</button>
            <a href={`${import.meta.env.BASE_URL}privacy.html`} target="_blank" rel="noreferrer">{copy.policy}</a>
          </div>
        </section>
      </div>,
      document.body,
    )}
  </>
}
